import {
  Component,
  DestroyRef,
  Injector,
  booleanAttribute,
  input,
  linkedSignal,
  viewChild,
  Signal,
  InputSignal,
  InputSignalWithTransform,
  OnInit,
  OnDestroy,
  signal,
  WritableSignal,
  inject,
  ElementRef,
  computed,
  output,
  effect,
  untracked,
  forwardRef
} from '@angular/core';
import { FocusMonitor } from '@angular/cdk/a11y';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MAT_FORM_FIELD, MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  FormsModule,
  NgControl,
  NgForm,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { skip, Subject } from 'rxjs';

import { IconFlagComponent } from '../icon/icon-flag.component';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { NumberInputDirective } from '../input/number-input.directive';
import { SignalFormBridgeService } from '../form/interop/signal-form-bridge.service';

interface SeededInputSource {
  amount: string | null | undefined;
  currency: string | null | undefined;
  defaultCurrency: string;
}

interface SeededInputValue {
  amount: string;
  currency: string;
}

export class MaterialInputCurrency {
  constructor(
    public amount: string,
    public currency: string
  ) { }
}

@Component({
  selector: 'td-wb-input-currency-selector',
  templateUrl: 'input-currency-selector.component.html',
  styleUrl: 'input-currency-selector.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    IconFlagComponent,
    NumberInputDirective
  ],
  providers: [
    ValueAccessorProviderFactory(InputCurrencySelectorComponent),
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => InputCurrencySelectorComponent)
    }
  ],
  host: {
    '[attr.id]': 'id',
    '[class.td-wb-input-currency-selector]': 'true',
    '[class.td-wb-input-currency-selector--dropdown-closed]': '!onCurrencySelectOpen()'
  }
})
export class InputCurrencySelectorComponent implements ControlValueAccessor, MatFormFieldControl<MaterialInputCurrency>, OnInit, OnDestroy {
  static nextId: number = 0;
  readonly showErrorBeforeSubmit: InputSignal<boolean> = input<boolean>(true);
  readonly codes: InputSignal<Array<string>> = input(['CAD', 'USD']);

  readonly amountInput: InputSignal<string | null | undefined> = input<string | null | undefined>(undefined, {
    alias: 'amount'
  });

  readonly currencyInput: InputSignal<string | null | undefined> = input<string | null | undefined>(undefined, {
    alias: 'currency'
  });

  readonly size: InputSignal<string> = input<string>('16x11');
  readonly numberInputFormat: InputSignal<string> = input<string>('n2');
  readonly numberInputGroupSeparator: InputSignal<boolean> = input<boolean>(true);
  readonly inputName: InputSignal<string> = input<string>('td-wb-input-currency-selector');
  readonly placeholderInput: InputSignal<string> = input<string>('', { alias: 'placeholder' });
  readonly amountValidators: InputSignal<Array<ValidatorFn>> = input<Array<ValidatorFn>>([]);
  readonly currencyChange = output<string | null>();
  readonly hostIdInput: InputSignal<string> = input<string>(
    `td-wb-input-currency-selector-${InputCurrencySelectorComponent.nextId++}`,
    { alias: 'id' }
  );

  readonly requiredInput: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'required',
    transform: booleanAttribute
  });

  readonly disabledByInput: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'disabled',
    transform: booleanAttribute
  });

  readonly areaInput: Signal<HTMLInputElement> = viewChild.required<HTMLInputElement>('amount');
  readonly currencySelect: Signal<MatSelect> = viewChild.required<MatSelect>('currencySelect');
  private readonly currencySelectRef: Signal<ElementRef<HTMLElement>> = viewChild.required('currencySelect', {
    read: ElementRef
  });

  readonly parts: FormGroup<{
    amount: FormControl<string | null>;
    currency: FormControl<string | null>;
  }>;

  protected readonly formField = inject(MAT_FORM_FIELD, {
    optional: true
  });

  readonly onCurrencySelectOpen: WritableSignal<boolean> = signal(false);
  readonly stateChanges: Subject<void> = new Subject<void>();
  readonly touched: WritableSignal<boolean> = signal(false);
  readonly describedbyIds: WritableSignal<string | null> = signal(null);
  private readonly focusedState: WritableSignal<boolean> = signal(false);
  private readonly disabledState: Signal<boolean> = computed(() => !!this.disabledByInput() || this._disabledByCva());
  private readonly seededInputValues: WritableSignal<SeededInputValue> = linkedSignal({
    source: computed(() => ({
      amount: this.amountInput(),
      currency: this.currencyInput(),
      defaultCurrency: this.codes()[0] ?? ''
    })),
    computation(source: SeededInputSource, previous: { value: SeededInputValue } | undefined): SeededInputValue {
      const previousValue = previous?.value ?? {
        amount: '',
        currency: source.defaultCurrency
      };
      return {
        amount: source.amount === undefined ? previousValue.amount : (source.amount ?? ''),
        currency: source.currency === undefined ? previousValue.currency : (source.currency ?? '')
      };
    }
  });

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly bridgeService = inject(SignalFormBridgeService);
  private readonly injector: Injector = inject(Injector);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly parentForm: NgForm | null = inject(NgForm, { optional: true });
  private readonly parentFormGroup: FormGroupDirective | null = inject(FormGroupDirective, { optional: true });

  /** Single source of truth. Written by `writeValue()` (no echo) and by `updateValue()`. */
  private readonly _value: WritableSignal<MaterialInputCurrency | null> = signal(null);
  private readonly _disabledByCva: WritableSignal<boolean> = signal(false);
  private onChangeCallback: (value: MaterialInputCurrency | null) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};
  private ngControlRef: NgControl | null = null;
  private isNgControlResolved: boolean = false;
  private isOuterValidatorAttached: boolean = false;

  get value(): MaterialInputCurrency | null {
    return this._value();
  }

  /** Required by `MatFormFieldControl`. Resolved lazily — see `resolveNgControl()`. */
  get ngControl(): NgControl | null {
    return this.resolveNgControl();
  }

  readonly shouldLabelFloat = true;
  get id(): string { return this.hostIdInput(); }
  get focused(): boolean { return this.focusedState(); }
  readonly empty = false;
  get placeholder(): string { return this.placeholderInput(); }
  get required(): boolean { return this.requiredInput(); }
  get disabled(): boolean { return this.disabledState(); }

  get errorState(): boolean {
    const ngControl = this.resolveNgControl();
    const parent = this.parentForm || this.parentFormGroup;
    const hasBeenTouched = this.touched() || !!ngControl?.touched;
    const shouldShowErrors =
      (hasBeenTouched && this.showErrorBeforeSubmit()) || !!parent?.submitted;

    return (ngControl?.control?.invalid ?? this.parts.invalid) && shouldShowErrors;
  }

  constructor() {
    this.parts = inject(FormBuilder).group({
      amount: ['', this.amountValidators()],
      currency: [this.codes()[0] ?? '']
    });

    effect(() => {
      // effect: updates amount validators when requiredInput changes — FormControl.setValidators()
      // is an imperative Angular Forms API that cannot be driven by a template binding.
      const required = this.requiredInput();
      let validators = this.amountValidators();
      if (required) {
        validators = [Validators.required, ...validators];
      }
      this.parts.controls.amount.setValidators(validators);
      this.parts.controls.amount.updateValueAndValidity();
    });

    effect(() => {
      // effect: propagates MatFormField state signals to the stateChanges Subject —
      // MatFormField observes stateChanges to update its host bindings imperatively.
      this.placeholderInput();
      this.requiredInput();
      this.disabledState();
      this.focusedState();
      untracked(() => this.stateChanges.next());
    });

    effect(() => {
      // effect: enables/disables the internal FormGroup to match the disabled signal —
      // FormGroup.disable/enable() is an imperative API not driven by template bindings.
      if (this.disabledState()) {
        untracked(() => this.parts.disable());
      } else {
        untracked(() => this.parts.enable());
      }
    });

    toObservable(this.seededInputValues).pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((seededValues: { amount: string; currency: string }): void => {
        this.setAmountCurrencyValue(seededValues.amount, seededValues.currency, false);
      });

    this.parts.statusChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.stateChanges.next();
        this.syncOuterControlErrors();
      });

    this.parentForm?.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.stateChanges.next();
      });

    this.parentFormGroup?.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.stateChanges.next();
      });

    this.parts.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        const amount: string | null | undefined = this.parts.value.amount;
        const currency: string | null | undefined = this.parts.value.currency;
        const nextSeed = {
          amount: amount ?? '',
          currency: currency ?? ''
        };
        const previousSeed = this.seededInputValues();
        if (previousSeed.amount !== nextSeed.amount || previousSeed.currency !== nextSeed.currency) {
          this.seededInputValues.set(nextSeed);
        }
        const value: MaterialInputCurrency | null = amount === null || amount === undefined || amount === ''
          ? null
          : new MaterialInputCurrency(amount, currency || '');
        this.updateValue(value);
      });
  }

  ngOnInit(): void {
    if (this.amountInput() !== undefined || this.currencyInput() !== undefined) {
      const seededValues = this.seededInputValues();
      this.setAmountCurrencyValue(seededValues.amount, seededValues.currency, false);
    }

    this.attachOuterValidatorIfPossible();
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  onFocusIn(): void {
    if (!this.focusedState()) {
      this.focusedState.set(true);
    }
  }

  onFocusOut(event: FocusEvent): void {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched.set(true);
      this.focusedState.set(false);
      this.onTouchedCallback();
    }
  }

  setDescribedByIds(ids: Array<string>): void {
    this.describedbyIds.set(ids.join(' '));
  }

  onContainerClick(event: MouseEvent): void {
    const select = this.currencySelect();
    const target = event.target as Element | null;
    const selectHost = this.currencySelectRef().nativeElement;

    if (target === selectHost) {
      // Screen readers (e.g. Android TalkBack) dispatch their activation click directly on
      // the <mat-select> host element — both the first time (to open the panel) and again
      // on subsequent activations (e.g. to dismiss it), because the host keeps accessibility
      // focus throughout the interaction (combobox + aria-activedescendant pattern). Neither
      // gesture reaches MatSelect's own internal trigger click handler, which only listens on
      // the inner `.mat-mdc-select-trigger`, so both opening and closing must be handled here.
      select.focus();
      if (select.panelOpen) {
        select.close();
        this.onCurrencySelectOpen.set(false);
      } else {
        select.open();
      }
      return;
    }

    if (select.panelOpen || (target && selectHost.contains(target))) {
      // A real click on the visible (closed) trigger is a descendant of `selectHost`, not
      // `selectHost` itself, and is already handled by MatSelect's own trigger click handler
      // before this bubbles up here — so once open, any further click still targeting the
      // select must be a no-op rather than re-opening or re-closing it a second time.
      return;
    }

    this.focusMonitor.focusVia(this.areaInput(), 'program');
  }

  handleInput(): void {
    this.onChangeCallback(this.value);
  }

  onCurrencySelectOpenedChange(event: boolean): void {
    this.onCurrencySelectOpen.set(event);
  }

  onCurrencySelectionChange(currency: string | null): void {
    this.currencyChange.emit(currency);
    // Screen readers (e.g. Android TalkBack) can select an option without Material's own
    // internal panelOpen/isUserInput bookkeeping being in sync at that point, so the built-in
    // close-on-select logic in MatSelect can be skipped. Closing here is a safe no-op otherwise.
    // `focus()` restores focus to the trigger (mirroring MatSelect's own close+focus pairing)
    // and the signal is updated synchronously so the trigger arrow doesn't wait on MatSelect's
    // own `openedChange`, which is emitted a microtask later via `Promise.resolve().then(...)`.
    const select = this.currencySelect();
    if (select.panelOpen) {
      select.close();
      select.focus();
      this.onCurrencySelectOpen.set(false);
    }
  }

  writeValue(value: MaterialInputCurrency | null): void {
    const amount = value?.amount ?? '';
    const currency = value?.currency ?? this.parts.controls.currency.value ?? this.codes()[0] ?? '';
    this.setAmountCurrencyValue(amount, currency, false);
  }

  registerOnChange(fn: (value: MaterialInputCurrency | null) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
  }

  private syncOuterControlErrors(): void {
    // Self-healing: the validator may not have attached yet in ngOnInit (see
    // attachOuterValidatorIfPossible), so retry here before revalidating.
    this.attachOuterValidatorIfPossible();
    // No-op under Signal Forms (validation is schema-derived); a real revalidation under
    // classic forms, where it re-runs the validator injected above.
    this.bridgeService.revalidateCurrentControl(this.resolveNgControl());
  }

  /**
   * Injects a validator onto the *outer* control that republishes the internal `amount`
   * control's errors, so a parent form sees min/max/etc. violations. Attempted from
   * `ngOnInit()`, and retried from `syncOuterControlErrors()` (invoked on every
   * `parts.statusChanges` emission) because `FormControlName` does not always finish
   * linking `.control` before our own `ngOnInit()` runs — depends on directive matching
   * order at the host element, which changed once this component started declaring itself
   * as `NG_VALUE_ACCESSOR` (see `ValueAccessorProviderFactory`). Guarded through the bridge:
   * `ngControl?.control` is ALWAYS truthy under Signal Forms (`InteropNgControl.control =
   * this`), and the shim implements neither `setValidators` nor `events`, so an unguarded
   * call throws. Skipping is correct there — Signal Forms derives validity from the schema,
   * so injecting a validator has no meaning.
   */
  private attachOuterValidatorIfPossible(): void {
    if (this.isOuterValidatorAttached) {
      return;
    }

    const control = this.bridgeService.getMutableControl(this.resolveNgControl());
    if (!control) {
      return;
    }

    this.isOuterValidatorAttached = true;
    const existingValidator = control.validator;
    control.setValidators([
      ...(existingValidator ? [existingValidator] : []),
      (): ReturnType<ValidatorFn> => this.parts.controls.amount.errors
    ]);
    control.updateValueAndValidity({ emitEvent: false });

    control.events.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.stateChanges.next();
      });
  }

  /**
   * Resolved lazily: injecting NgControl eagerly while this component is also registered as
   * its own NG_VALUE_ACCESSOR creates an NG0200 circular dependency under `[formField]`.
   */
  private resolveNgControl(): NgControl | null {
    if (!this.isNgControlResolved) {
      this.ngControlRef = this.bridgeService.getNgControl(this.injector);
      this.isNgControlResolved = true;
    }

    return this.ngControlRef;
  }

  private updateValue(value: MaterialInputCurrency | null, emitChange: boolean = true): void {
    const current = this.value;
    if (
      value === current ||
      (value?.amount === current?.amount &&
        value?.currency === current?.currency)
    ) {
      return;
    }
    this._value.set(value);
    if (emitChange) {
      this.onChangeCallback(value);
    }
  }

  private setAmountCurrencyValue(amount: string, currency: string, emitChange: boolean): void {
    const currentAmount = this.parts.controls.amount.value ?? '';
    const currentCurrency = this.parts.controls.currency.value ?? '';
    const value: MaterialInputCurrency | null = amount === ''
      ? null
      : new MaterialInputCurrency(amount, currency);

    if (currentAmount === amount && currentCurrency === currency) {
      this.updateValue(value, emitChange);
      return;
    }

    this.parts.setValue({ amount, currency }, { emitEvent: false });
    this.updateValue(value, emitChange);
    this.stateChanges.next();
  }
}
